
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { BankAccountSummary } from "@/types/accounts";
import { ServiceRegistry } from "@/services/ServiceRegistry";
import { toast } from "sonner";
import { useTranslations } from 'use-intl';
import { useConsentContextOptional } from '@/components/providers/ConsentProvider';
import { renewalRequestIfAllAccessLost } from '@/lib/renewalRequest';
import { useAccountsAfterRenewal } from '@/hooks/useAccountsAfterRenewal';
import { translateOutsideReact } from '@/i18n/translateOutsideReact';

export function useBankAccounts() {
  const t = useTranslations('consultantToasts');
  // Generate a stable ID for this hook instance only once
  const hookIdRef = useRef<string | null>(null);
  if (hookIdRef.current === null) {
    hookIdRef.current = Math.random().toString(36).substring(7);
  }
  const hookId = hookIdRef.current;

  const [accounts, setAccounts] = useState<BankAccountSummary[]>([]);
  useAccountsAfterRenewal(setAccounts);
  const [loadingAccounts, setLoadingAccounts] = useState<boolean>(true);
  const [accountsError, setAccountsError] = useState<string | null>(null);

  // Try to get the consent context, but it's optional
  const consentContext = useConsentContextOptional();

  // Extract and memoize the functions to prevent infinite loops
  const contextSetError = consentContext?.setConsentRequiredError;
  const contextHandleRefresh = consentContext?.handleConsentRefreshSuccess;

  // Use provider context if available, otherwise handle consent errors locally
  const [localConsentError, setLocalConsentError] = useState<any>(null);

  const consentRequiredError = consentContext?.consentRequiredError ?? localConsentError;

  // Stabilize the setter function to prevent infinite loops
  const setConsentRequiredError = useCallback((error: any) => {
    if (contextSetError) {
      contextSetError(error);
    } else {
      setLocalConsentError(error);
    }
  }, [contextSetError]);

  // Get the account service - memoize to prevent recreating the hook
  const accountService = useMemo(() => {
    return ServiceRegistry.getAccountService();
  }, []);

  // Load bank accounts
  const loadAccounts = useCallback(async () => {
    setLoadingAccounts(true);
    setAccountsError(null);
    
    try {
      // Load from API
      const apiAccountsResponse = await accountService.getBankAccounts();
      
      if (apiAccountsResponse && apiAccountsResponse.length > 0) {
        setAccounts(apiAccountsResponse);
        const renewal = renewalRequestIfAllAccessLost(apiAccountsResponse);
        if (renewal) setTimeout(() => setConsentRequiredError(renewal), 0);
      } else {
        setAccounts([]);
      }
    } catch (err: unknown) {
      console.error("Failed to load accounts:", err);
      
      // AccountApiClient has already shown a toast and reported the failure; this state is what
      // lets a page tell "failed to load" apart from "no accounts" (FIN-802).
      setAccountsError(translateOutsideReact('consultantErrors.fallback.bankAccountsLoad'));
    } finally {
      setLoadingAccounts(false);
    }
  }, [accountService, setConsentRequiredError]);

  // Load accounts on component mount
  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  // Handle consent refresh success
  const handleConsentRefreshSuccess = useCallback(() => {
    // Use the stabilized setter function
    setConsentRequiredError(null);
    // Trigger account reload
    loadAccounts();
  }, [loadAccounts, setConsentRequiredError]);


  // Connect bank handler
  const handleConnectBank = async (newAccounts: BankAccountSummary[]) => {
    if (!newAccounts || newAccounts.length === 0) return;

    // Reload all accounts from server to include both existing and newly connected accounts
    await loadAccounts();

    toast.success(t('bankAccounts.connected', { count: newAccounts.length }));
  };

  return {
    accounts,
    setAccounts,
    loadingAccounts,
    accountsError,
    consentRequiredError,
    loadAccounts,
    handleConnectBank,
    handleConsentRefreshSuccess
  };
}
